import React, { useState, useEffect, useCallback } from 'react';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Loader2, FileDown, Trash2, Upload, ShieldCheck, FolderOpen, X, Briefcase, Mail, Copy } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', slate: '#5C6B7A', green: '#1D9E75', amber: '#B27516', red: '#C0392B', line: 'rgba(136,150,168,0.12)' };

function SpreadBars({ theirs, ours }) {
  if (theirs === null || theirs === undefined) return <span className="text-xs" style={{ color: C.slate }}>—</span>;
  return (
    <div className="space-y-1 min-w-[140px]">
      <div className="flex items-center gap-2">
        <div className="h-2 flex-1 rounded-sm" style={{ background: '#E8EDF2' }}>
          <div className="h-2 rounded-sm" style={{ width: `${Math.min(100, theirs)}%`, background: C.red }} />
        </div>
        <span className="text-[11px] font-mono w-12 text-right" style={{ color: C.red }}>{theirs}%</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="h-2 flex-1 rounded-sm" style={{ background: '#E8EDF2' }}>
          <div className="h-2 rounded-sm" style={{ width: `${Math.min(100, ours || 0)}%`, background: C.teal }} />
        </div>
        <span className="text-[11px] font-mono w-12 text-right" style={{ color: C.teal }}>{ours ?? '—'}{ours !== null && ours !== undefined ? '%' : ''}</span>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, color }) {
  return (
    <Card className="p-4 rounded-md bg-white" style={{ border: `1px solid ${C.line}`, borderLeft: `3px solid ${color || C.blue}` }}>
      <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{label}</p>
      <p className="font-mono text-2xl font-bold mt-1" style={{ color: color || C.navy }}>{value}</p>
      {sub && <p className="text-[11px] mt-0.5" style={{ color: C.slate }}>{sub}</p>}
    </Card>
  );
}

function copyText(text, okMsg) {
  const fallback = () => {
    const ta = document.createElement('textarea');
    ta.value = text; document.body.appendChild(ta); ta.select();
    try { document.execCommand('copy'); toast.success(okMsg); } catch { toast.error('Copy failed — select and copy manually'); }
    document.body.removeChild(ta);
  };
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(() => toast.success(okMsg)).catch(fallback);
  } else fallback();
}

function followUpEmail(review) {
  const h = review.analysis?.headline || {};
  const p = review.prospect_name;
  return `Subject: The consistency review — one number worth a second look

Hi,

Thanks for the session. One number from ${p}'s review worth sitting with: comparable risks in your book were priced up to ${h.their_max_spread_pct ?? '—'}% apart, with a median spread of ${h.their_median_spread_pct ?? '—'}% — the same submissions, scored by a single consistent model, came in at ${h.our_median_spread_pct ?? '—'}%.

${h.cohorts_with_mixed_decisions || 0} of the comparable groups also had mixed outcomes — the same risk quoted by one underwriter and declined by another.

The full report is with your team now; the limitations are on the front page and the underwriters stay anonymous. If it raises questions, the most useful next step is usually a short session with whoever owns the pricing decision — happy to walk through where the spread concentrates.

Either way, the data deletes on your word, in one action.
`;
}

function SalesKitCard({ api }) {
  const [prospect, setProspect] = useState('');
  const [fee, setFee] = useState('4500');
  const [busy, setBusy] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);

  const download = async (path, filename, opts = {}) => {
    try {
      const resp = opts.post
        ? await api.post(path, opts.body, { responseType: 'blob' })
        : await api.get(path, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      let detail = 'Download failed';
      try { detail = JSON.parse(await e.response.data.text()).detail; } catch { /* default */ }
      toast.error(detail);
    }
  };

  const genericFollowUp = `Subject: The consistency review — one number worth a second look

Hi,

Thanks for the session. One number from the review worth sitting with: comparable risks in your book were priced up to [max spread]% apart, with a median spread of [median]% — the same submissions, scored by a single consistent model, came in at [model median]%.

[N] of the comparable groups also had mixed outcomes — the same risk quoted by one underwriter and declined by another.

The full report is with your team now; the limitations are on the front page and the underwriters stay anonymous. If it raises questions, the most useful next step is usually a short session with whoever owns the pricing decision — happy to walk through where the spread concentrates.

Either way, the data deletes on your word, in one action.
`;

  return (
    <Card className="p-5 rounded-md bg-white space-y-3" style={{ border: `1px solid ${C.line}` }} data-testid="sales-kit-card">
      <div className="flex items-center gap-2">
        <Briefcase size={14} style={{ color: C.blue }} />
        <p className="text-[10px] uppercase tracking-[0.15em] font-medium" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>Sales kit — $4,500 + GST fixed-fee diagnostic</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => download('/consistency/kit/data-request.pdf', 'Consistency_Review_Data_Request.pdf')} className="rounded-md text-xs" data-testid="kit-data-request-btn">
          <FileDown className="w-3.5 h-3.5 mr-1.5" /> Data request one-pager
        </Button>
        <Button size="sm" variant="outline" onClick={() => download('/consistency/kit/template.csv', 'Consistency_Review_Template.csv')} className="rounded-md text-xs" data-testid="kit-template-btn">
          <FileDown className="w-3.5 h-3.5 mr-1.5" /> Spreadsheet template
        </Button>
        <Button size="sm" variant="outline" onClick={() => copyText(genericFollowUp, '48-hour follow-up email copied — send it two days after the meeting')} className="rounded-md text-xs" data-testid="kit-followup-btn">
          <Mail className="w-3.5 h-3.5 mr-1.5" /> Copy follow-up email
        </Button>
        <button onClick={() => setShowTemplate(!showTemplate)} className="text-[11px] underline self-center" style={{ color: C.slate }} data-testid="kit-letter-toggle">
          {showTemplate ? 'close letter' : 'engagement letter…'}
        </button>
      </div>
      {showTemplate && (
        <div className="flex flex-wrap items-center gap-2 pt-2" style={{ borderTop: `1px solid ${C.line}` }} data-testid="kit-letter-form">
          <Input value={prospect} onChange={e => setProspect(e.target.value)} placeholder="Prospect name (printed on the letter)" className="rounded-sm text-xs h-8 w-64" data-testid="kit-letter-prospect" />
          <div className="flex items-center gap-1">
            <span className="text-xs" style={{ color: C.slate }}>$</span>
            <Input value={fee} onChange={e => setFee(e.target.value)} className="rounded-sm text-xs h-8 w-20 font-mono" data-testid="kit-letter-fee" />
            <span className="text-xs" style={{ color: C.slate }}>+ GST</span>
          </div>
          <Button size="sm" disabled={busy || !prospect.trim()} className="rounded-md text-xs" style={{ background: C.navy }} data-testid="kit-letter-generate"
            onClick={async () => { setBusy(true); await download('/consistency/kit/engagement-letter.pdf', 'Consistency_Review_Engagement_Letter.pdf', { post: true, body: { prospect_name: prospect, fee: parseFloat(fee) || 4500 } }); setBusy(false); }}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Generate letter (PDF)'}
          </Button>
          <p className="text-[10px] w-full" style={{ color: C.slate }}>Print-only — not executable until NDA and privacy terms are settled. Fee floor $2,500. Fee credits against year-one licence within 90 days.</p>
        </div>
      )}
    </Card>
  );
}

function ReviewDetail({ review, api, onClose, onDeleted }) {
  const a = review.analysis || {};
  const h = a.headline || {};
  const [busy, setBusy] = useState(false);

  const downloadPdf = async () => {
    try {
      const resp = await api.get(`/consistency/reviews/${review.review_id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const el = document.createElement('a');
      el.href = url; el.download = `Consistency_Review_${review.prospect_name.replace(/ /g, '_')}.pdf`; el.click();
      URL.revokeObjectURL(url);
      toast.success('Consistency Review PDF downloaded');
    } catch { toast.error('Could not download the PDF'); }
  };

  const deleteReview = async () => {
    if (!window.confirm(`Delete everything for ${review.prospect_name}? One action — file rows, analysis, the lot.`)) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/consistency/reviews/${review.review_id}`);
      toast.success(`Deleted — ${data.confirmation.records_removed} record(s) removed, confirmation logged`);
      onDeleted();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
    setBusy(false);
  };

  return (
    <div className="space-y-4 stagger-in" data-testid="consistency-review-detail">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium" style={{ color: C.navy }}>{review.prospect_name}</h2>
          <p className="text-xs" style={{ color: C.slate }}>{review.line?.replace(/_/g, ' ')} · {review.row_count} submissions · {review.filename}</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={downloadPdf} className="rounded-md text-xs" style={{ background: C.navy }} data-testid="review-pdf-btn">
            <FileDown className="w-3.5 h-3.5 mr-1.5" /> Branded PDF
          </Button>
          <Button size="sm" variant="outline" onClick={() => copyText(followUpEmail(review), "Follow-up email copied with this review's numbers — send it 48 hours after the meeting")} className="rounded-md text-xs" data-testid="review-followup-btn">
            <Copy className="w-3.5 h-3.5 mr-1.5" /> Follow-up email
          </Button>
          <Button size="sm" variant="outline" onClick={deleteReview} disabled={busy} className="rounded-md text-xs" style={{ color: C.red, borderColor: 'rgba(192,57,43,0.3)' }} data-testid="review-delete-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1.5" />} Delete everything
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose} className="rounded-md text-xs" data-testid="review-close-btn"><X className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="p-3 rounded-md text-xs" style={{ background: '#FDF6E3', border: '1px solid #E8D9A0', color: '#6B5D1F' }} data-testid="review-limitations">
        <b>Read this first.</b> {(a.limitations || []).map((l, i) => <span key={i} className="block mt-1">• {l}</span>)}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Submissions analysed" value={h.rows_analysed}
          sub={`${h.rows_in_comparable_cohorts} kept in comparable groups (${a.survival?.survival_rate}%)`} />
        <Kpi label="Their median spread" value={h.their_median_spread_pct !== null && h.their_median_spread_pct !== undefined ? `${h.their_median_spread_pct}%` : '—'}
          sub={`max ${h.their_max_spread_pct ?? '—'}% · ${h.cohorts_priced_30pct_apart} group(s) 30%+ apart`} color={C.red} />
        <Kpi label="Model spread, same rows" value={h.our_median_spread_pct !== null && h.our_median_spread_pct !== undefined ? `${h.our_median_spread_pct}%` : '—'}
          sub="the consistency benchmark" color={C.teal} />
        <Kpi label="Decision agreement" value={h.mean_agreement_pct !== null && h.mean_agreement_pct !== undefined ? `${h.mean_agreement_pct}%` : '—'}
          sub={`${h.cohorts_with_mixed_decisions} group(s) with mixed outcomes`} color={C.amber} />
      </div>

      <Card className="p-5 rounded-md bg-white" style={{ border: `1px solid ${C.line}` }}>
        <p className="text-[10px] uppercase tracking-[0.15em] font-medium mb-1" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>Where decisions cluster — and where they scatter</p>
        <p className="text-[11px] mb-3" style={{ color: C.slate }}>Red: the spread of <b>their</b> premiums within each comparable group. Teal: the model's spread on the same submissions ({a.spread_basis}).</p>
        <table className="w-full text-xs" data-testid="review-cohorts-table">
          <thead><tr className="text-left text-[10px] uppercase tracking-wider" style={{ color: C.slate }}>
            <th className="py-1.5 pr-2">Comparable group</th><th className="py-1.5 pr-2">Their outcomes</th><th className="py-1.5 pr-2">Spread — them vs model</th><th className="py-1.5">Separating factors</th></tr></thead>
          <tbody>
            {(a.cohorts || []).map(c => (
              <tr key={c.cohort_id} style={{ borderTop: `1px solid ${C.line}` }} data-testid={`review-cohort-${c.cohort_id}`}>
                <td className="py-2.5 pr-2">
                  <b style={{ color: C.navy }}>{c.label}</b>
                  <p className="text-[10px] mt-0.5" style={{ color: C.slate }}>incl. {(c.refs || []).slice(0, 3).join(', ')}</p>
                </td>
                <td className="py-2.5 pr-2" style={{ color: C.slate }}>
                  {Object.entries(c.their_outcomes || {}).map(([k, v]) => `${k} ${v}`).join(' · ')}
                  <p className="text-[10px] mt-0.5">agreement {c.agreement_pct}%</p>
                </td>
                <td className="py-2.5 pr-2"><SpreadBars theirs={c.their_spread_pct} ours={c.our_spread_pct} /></td>
                <td className="py-2.5" style={{ color: C.slate }}>{(c.explaining_factors || []).join(', ') || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {(a.underwriters || []).length > 0 && (
        <Card className="p-5 rounded-md bg-white" style={{ border: `1px solid ${C.line}` }}>
          <p className="text-[10px] uppercase tracking-[0.15em] font-medium mb-2" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>Underwriters, anonymised</p>
          <table className="w-full text-xs" data-testid="review-underwriters-table">
            <thead><tr className="text-left text-[10px] uppercase tracking-wider" style={{ color: C.slate }}>
              <th className="py-1.5">Underwriter</th><th className="py-1.5">Submissions</th><th className="py-1.5">Quoted</th><th className="py-1.5">Decline rate</th><th className="py-1.5">Premium index</th></tr></thead>
            <tbody>
              {a.underwriters.map(u => (
                <tr key={u.code} style={{ borderTop: `1px solid ${C.line}` }}>
                  <td className="py-2 font-bold" style={{ color: C.navy }}>Underwriter {u.code}</td>
                  <td className="py-2" style={{ color: C.slate }}>{u.rows}</td>
                  <td className="py-2" style={{ color: C.slate }}>{u.quoted}</td>
                  <td className="py-2" style={{ color: C.slate }}>{u.decline_rate}%</td>
                  <td className="py-2 font-mono" style={{ color: u.premium_index > 115 || u.premium_index < 85 ? C.red : C.navy }}>
                    {u.premium_index ?? '—'} <span className="text-[10px] font-sans" style={{ color: C.slate }}>(100 = occupation-adjusted median)</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[10px] mt-2" style={{ color: C.slate }}>Shown as A, B, C — identities stay with the prospect. No individual is benchmarked on fewer than 15 comparable priced submissions.</p>
        </Card>
      )}
    </div>
  );
}

export default function ConsistencyReviewPage() {
  const api = useApi();
  const { user } = useAuth();
  const [classes, setClasses] = useState({});
  const [reviews, setReviews] = useState(null);
  const [active, setActive] = useState(null);
  const [prospect, setProspect] = useState('');
  const [line, setLine] = useState('public_liability');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const isAdmin = ['platform_owner', 'mga_admin'].includes(user?.role);

  const load = useCallback(() => {
    api.get('/consistency/reviews').then(r => setReviews(r.data.reviews)).catch(() => setReviews([]));
  }, [api]);

  useEffect(() => {
    if (!isAdmin) return;
    load();
    api.get('/insurance-classes').then(r => setClasses(r.data || {})).catch(() => {});
  }, [load, api, isAdmin]);

  if (!isAdmin) {
    return (
      <Card className="p-6 rounded-md bg-white max-w-xl" style={{ border: `1px solid ${C.line}` }} data-testid="consistency-restricted-note">
        <p className="text-sm" style={{ color: C.navy }}><b>Restricted.</b> Consistency reviews hold prospect data and are limited to the platform owner and MGA admins.</p>
      </Card>
    );
  }

  const upload = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('prospect_name', prospect); fd.append('line', line); fd.append('file', file);
      const { data } = await api.post('/consistency/reviews', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Review ready — ${data.row_count} submissions analysed`);
      setActive(data); setProspect(''); setFile(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not analyse the file'); }
    setBusy(false);
  };

  const openReview = async (id) => {
    try {
      const { data } = await api.get(`/consistency/reviews/${id}`);
      setActive(data);
    } catch { toast.error('Could not open the review'); }
  };

  if (active) return <ReviewDetail review={active} api={api} onClose={() => setActive(null)} onDeleted={() => { setActive(null); load(); }} />;

  return (
    <div className="space-y-4 max-w-4xl" data-testid="consistency-review-page">
      <div className="flex items-start gap-2 p-3 rounded-md" style={{ background: 'rgba(0,180,216,0.06)', border: '1px solid rgba(0,180,216,0.25)' }} data-testid="consistency-privacy-banner">
        <ShieldCheck size={15} className="mt-0.5 shrink-0" style={{ color: C.teal }} />
        <p className="text-xs" style={{ color: C.navy }}>
          <b>Their own book, their own mirror.</b> Upload a prospect's historical quote register (CSV / XLSX, business-level data only) —
          the model scores every row and shows where their underwriters priced comparable risks apart. The file is <b>quarantined</b>:
          never used for training, deleted in one action, confirmation logged.
        </p>
      </div>

      <SalesKitCard api={api} />

      <Card className="p-5 rounded-md bg-white space-y-3" style={{ border: `1px solid ${C.line}` }}>
        <p className="text-[10px] uppercase tracking-[0.15em] font-medium" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>New consistency review</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <Input value={prospect} onChange={e => setProspect(e.target.value)} placeholder="Prospect / agency name" className="rounded-sm text-sm" data-testid="review-prospect-input" />
          <select value={line} onChange={e => setLine(e.target.value)} data-testid="review-line-select"
            className="text-xs rounded-md px-2.5 py-2 bg-white" style={{ border: `1px solid ${C.line}`, color: C.navy }}>
            {Object.keys(classes).length === 0 && <option value={line}>Loading classes…</option>}
            {Object.values(classes).filter(c => c.active).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input type="file" accept=".csv,.xlsx" data-testid="review-file-input"
            onChange={e => setFile(e.target.files?.[0] || null)}
            className="text-xs pt-1.5 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-[#0D1B2A] file:text-white" />
        </div>
        <Button onClick={upload} disabled={busy || !prospect.trim() || !file} className="rounded-md text-xs" style={{ background: C.teal, color: C.navy, fontWeight: 600 }} data-testid="review-upload-btn">
          {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Scoring every row…</> : <><Upload className="w-3.5 h-3.5 mr-1.5" /> Analyse the book</>}
        </Button>
      </Card>

      {reviews === null ? (
        <div className="space-y-2">{[...Array(2)].map((_, i) => <Skeleton key={i} className="h-16 rounded-md" />)}</div>
      ) : reviews.length > 0 ? (
        <div className="space-y-2" data-testid="reviews-list">
          {reviews.map(r => (
            <Card key={r.review_id} className="p-4 rounded-md bg-white flex items-center justify-between gap-3" style={{ border: `1px solid ${C.line}` }} data-testid={`review-row-${r.review_id}`}>
              <div>
                <p className="text-sm font-medium" style={{ color: C.navy }}>{r.prospect_name}</p>
                <p className="text-[11px]" style={{ color: C.slate }}>{r.line?.replace(/_/g, ' ')} · {r.row_count} rows · {r.created_at?.slice(0, 10)} · quarantined</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => openReview(r.review_id)} className="rounded-md text-xs shrink-0" data-testid={`review-open-${r.review_id}`}>
                <FolderOpen className="w-3.5 h-3.5 mr-1.5" /> Open
              </Button>
            </Card>
          ))}
        </div>
      ) : (
        <p className="text-xs" style={{ color: C.slate }}>No reviews yet — upload a prospect book above.</p>
      )}
    </div>
  );
}
